import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';

export default function InputDropdown({
  items = [], // string[] or [{value, label}]
  onChange, // standard input onChange
  onItemSelect, // optional (item) => void
  renderItem, // optional (item) => ReactNode
  className,
  contentClassName,
  id,
  name,
  value,
  defaultValue,
  placeholder,
  disabled,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const [width, setWidth] = useState();

  // keep popover width equal to input width
  useLayoutEffect(() => {
    if (!inputRef.current) return;
    const update = () =>
      setWidth(inputRef.current.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(inputRef.current);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const normalized = items.map((it) =>
    typeof it === 'string' ? { value: it, label: it } : it,
  );

  const handleSelect = (item) => {
    if (onItemSelect) {
      onItemSelect(item);
    } else if (onChange) {
      onChange({ target: { value: item.value, name, id } });
    }
    setOpen(false);
    queueMicrotask(() => inputRef.current?.focus());
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger>
        <Input
          ref={inputRef}
          id={id}
          name={name}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          {...rest}
          onChange={onChange} // typing always works
          onClick={() => setOpen((o) => !o)} // click toggles open/close
        />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={'p-0 overflow-hidden rounded-md ' + contentClassName} // 👈 rounded here, hide overflow
        style={{ width }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {normalized.length === 0 ? (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            Nėra pasiūlymų
          </div>
        ) : (
          <ul className="py-1 max-h-64 overflow-auto">
            {normalized.map((item, i) => (
              <li key={item.value ?? i}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-accent hover:text-accent-foreground"
                  onClick={() => handleSelect(item)}
                >
                  {renderItem
                    ? renderItem(item)
                    : item.label ?? String(item.value)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
